/**
 * The exception alert feed and its history (KAN-43 / KAN-81).
 *
 * Two things on one screen, deliberately. The feed answers "what needs me now";
 * the history answers "are we actually acting on these" — and KAN-81 asks for
 * both. Separating them into two pages would put the second behind a click
 * nobody takes, and the second is the one that says whether the feature works.
 *
 * What is *not* here: any figure this page calculated. Every number below
 * arrives from the API, which selected it from the module that owns it. The
 * page formats and never computes — the same discipline the executive dashboard
 * follows, for the same reason.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ALERT_CODES,
  ALERT_META,
  ALERT_SEVERITY_META,
  type AlertCode,
  type AlertSeverity,
  type AlertStatus,
  type ThresholdUnit,
} from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { formatDateTime, formatMoney, formatRelative } from '../lib/format'
import { Alert as Banner } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

interface AlertRow {
  id: string
  code: AlertCode
  label: string
  description: string
  unit: ThresholdUnit
  severity: AlertSeverity
  status: AlertStatus
  resolution: string | null
  entityLabel: string | null
  link: string
  trippedValue: number
  firstTrippedValue: number
  thresholdAtTrigger: number
  triggeredAt: string
  lastNotifiedAt: string | null
  acknowledgedAt: string | null
  acknowledgedBy: string | null
  resolvedAt: string | null
  resolvedBy: string | null
  note: string | null
}

interface FeedResponse {
  alerts: AlertRow[]
  page: number
  pageCount: number
  total: number
}

interface HistoryRule {
  code: AlertCode
  label: string
  totalCount: number
  openCount: number
  resolvedCount: number
  ruleChangedCount: number
  meanHoursToResolve: number | null
  oldestOpenDays: number | null
}

/**
 * Severity to colour, and the mapping is the point rather than the palette.
 *
 * Three levels, three tones, used identically wherever an alert appears. A
 * critical alert that is red on this page and amber in the notification centre
 * would make somebody check twice, which is exactly the hesitation an alert
 * exists to remove.
 */
const SEVERITY_TONE: Record<AlertSeverity, Tone> = {
  critical: 'critical',
  warning: 'warning',
  info: 'neutral',
}

const STATUS_TONE: Record<AlertStatus, Tone> = {
  open: 'critical',
  acknowledged: 'accent',
  resolved: 'positive',
}

export function Alerts() {
  const { may } = useSession()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'unresolved' | 'open' | 'acknowledged' | 'resolved' | 'all'>(
    'unresolved'
  )
  const [code, setCode] = useState<AlertCode | ''>('')

  const canManage = may('alerts.manage')

  const feed = useQuery({
    queryKey: ['alerts', status, code],
    queryFn: () =>
      apiFetch<FeedResponse>(
        `/alerts?status=${status}${code ? `&code=${code}` : ''}`
      ),
  })

  const history = useQuery({
    queryKey: ['alerts', 'history'],
    queryFn: () => apiFetch<{ rules: HistoryRule[] }>('/alerts/history'),
  })

  const act = useMutation({
    mutationFn: ({ id, next }: { id: string; next: 'acknowledged' | 'resolved' }) =>
      apiFetch(`/alerts/${id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const evaluate = useMutation({
    mutationFn: () => apiFetch('/alerts/evaluate', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  })

  /**
   * Only the rules this role can be told about. Reading the shared matrix
   * rather than the API's response so the filter has options before the first
   * request returns — and it matches, because both read the same declaration.
   */
  const visibleCodes = ALERT_CODES.filter((c) => may(ALERT_META[c].permission))

  return (
    <>
      <PageHeader
        title="Exception alerts"
        description="Thresholds that have been crossed across trust, billing, receivables and payables. Each alert links to the record behind it."
        actions={
          canManage ? (
            <>
              <Button onClick={() => evaluate.mutate()} disabled={evaluate.isPending}>
                {evaluate.isPending ? 'Evaluating…' : 'Evaluate now'}
              </Button>
              <Link to="/alerts/settings">
                <Button variant="primary">Thresholds</Button>
              </Link>
            </>
          ) : null
        }
      />

      {evaluate.isSuccess && (
        <Banner tone="success" className="mb-4">
          Evaluation complete. Anything newly tripped is below.
        </Banner>
      )}

      {act.isError && (
        <Banner tone="error" className="mb-4">
          {(act.error as Error).message}
        </Banner>
      )}

      <Card className="mb-6">
        <CardHeader
          title="Feed"
          description="Critical first, then most recent. Acknowledging records that somebody has it; it does not close the alert, because the condition is still true."
          actions={
            <div className="flex flex-wrap items-end gap-3">
              <Field id="alert-status" label="Status">
                <Select id="alert-status" value={status} onChange={(e) => setStatus(e.target.value as never)}>
                  <option value="unresolved">Open &amp; acknowledged</option>
                  <option value="open">Open only</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                  <option value="all">All</option>
                </Select>
              </Field>
              <Field id="alert-type" label="Type">
                <Select id="alert-type" value={code} onChange={(e) => setCode(e.target.value as AlertCode | '')}>
                  <option value="">Every type</option>
                  {visibleCodes.map((c) => (
                    <option key={c} value={c}>
                      {ALERT_META[c].label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          }
        />
        <CardBody className="px-0 py-0">
          <TableWrap>
              <thead>
                <tr>
                  <Th>Alert</Th>
                  <Th>Subject</Th>
                  <Th className="text-right">Measured</Th>
                  <Th className="text-right">Threshold</Th>
                  <Th>Raised</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {feed.isLoading && (
                  <EmptyRow colSpan={7}>Loading…</EmptyRow>
                )}

                {feed.isError && (
                  <EmptyRow colSpan={7}>{(feed.error as Error).message}</EmptyRow>
                )}

                {feed.data?.alerts.length === 0 && (
                  <EmptyRow colSpan={7}>
                    {status === 'unresolved'
                      ? 'Nothing is tripping. That is the intended state of this screen.'
                      : 'No alerts match those filters.'}
                  </EmptyRow>
                )}

                {feed.data?.alerts.map((row) => (
                  <tr key={row.id} className="border-t border-ink-100">
                    <Td>
                      <div className="flex items-center gap-2">
                        <Badge tone={SEVERITY_TONE[row.severity]}>
                          {ALERT_SEVERITY_META[row.severity].label}
                        </Badge>
                        <span className="font-medium text-brand-900">{row.label}</span>
                      </div>
                    </Td>
                    <Td>
                      {/**
                       * The deep link KAN-81 requires. Built by the API from
                       * shared code, so this link and the one in the email go
                       * to the same place.
                       */}
                      <Link
                        to={row.link}
                        className="text-brand-700 underline-offset-2 hover:underline"
                      >
                        {row.entityLabel ?? 'The firm'}
                      </Link>
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatValue(row.unit, row.trippedValue)}
                      {row.trippedValue !== row.firstTrippedValue && (
                        /**
                         * Shown only when it has moved. The alert was raised at
                         * one figure and the condition has since worsened or
                         * eased; that movement is the useful signal, and
                         * printing "was X" on every row when X equals the
                         * current value is noise.
                         */
                        <span className="block text-xs text-ink-500">
                          was {formatValue(row.unit, row.firstTrippedValue)}
                        </span>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums text-ink-600">
                      {formatValue(row.unit, row.thresholdAtTrigger)}
                    </Td>
                    <Td>
                      <span title={formatDateTime(row.triggeredAt)}>
                        {formatRelative(row.triggeredAt)}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[row.status]}>{row.status}</Badge>
                      {row.acknowledgedBy && row.status === 'acknowledged' && (
                        <span className="block text-xs text-ink-500">
                          by {row.acknowledgedBy}
                        </span>
                      )}
                    </Td>
                    <Td className="text-right">
                      {row.status === 'open' && (
                        <Button
                          size="sm"
                          onClick={() => act.mutate({ id: row.id, next: 'acknowledged' })}
                          disabled={act.isPending}
                        >
                          Acknowledge
                        </Button>
                      )}
                      {row.status === 'acknowledged' && (
                        <Button
                          size="sm"
                          onClick={() => act.mutate({ id: row.id, next: 'resolved' })}
                          disabled={act.isPending}
                        >
                          Close
                        </Button>
                      )}
                      {row.status === 'resolved' && (
                        <span className="text-xs text-ink-500">
                          {row.resolution === 'rule_changed'
                            ? 'Rule changed'
                            : row.resolution === 'manual'
                              ? `Closed by ${row.resolvedBy ?? 'a person'}`
                              : 'Condition cleared'}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
          </TableWrap>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Time to resolution"
          description="Per rule, over every alert it has ever raised. Alerts closed by a threshold change are counted separately and excluded from the average — a rule loosened is not a problem solved."
        />
        <CardBody className="px-0 py-0">
          <TableWrap>
              <thead>
                <tr>
                  <Th>Rule</Th>
                  <Th className="text-right">Raised</Th>
                  <Th className="text-right">Open</Th>
                  <Th className="text-right">Resolved</Th>
                  <Th className="text-right">Mean time to resolve</Th>
                  <Th className="text-right">Oldest open</Th>
                  <Th className="text-right">Closed by a rule change</Th>
                </tr>
              </thead>
              <tbody>
                {history.data?.rules.length === 0 && (
                  <EmptyRow colSpan={7}>No alerts have been raised yet.</EmptyRow>
                )}

                {history.data?.rules.map((rule) => (
                  <tr key={rule.code} className="border-t border-ink-100">
                    <Td>{rule.label}</Td>
                    <Td className="text-right tabular-nums">{rule.totalCount}</Td>
                    <Td className="text-right tabular-nums">{rule.openCount}</Td>
                    <Td className="text-right tabular-nums">{rule.resolvedCount}</Td>
                    <Td className="text-right tabular-nums">
                      {/**
                       * A dash rather than a zero. "Nothing has been resolved
                       * yet" and "everything is resolved instantly" are
                       * opposite facts and a 0 would show the worse one as the
                       * better one.
                       */}
                      {rule.meanHoursToResolve === null
                        ? '—'
                        : rule.meanHoursToResolve < 48
                          ? `${rule.meanHoursToResolve} h`
                          : `${Math.round(rule.meanHoursToResolve / 24)} d`}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {rule.oldestOpenDays === null ? '—' : `${rule.oldestOpenDays} d`}
                    </Td>
                    <Td className="text-right tabular-nums text-ink-500">
                      {rule.ruleChangedCount || '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
          </TableWrap>
        </CardBody>
      </Card>
    </>
  )
}

/**
 * Formats a value in its rule's own unit.
 *
 * Money goes through the shared formatter, so an alert and the trust screen
 * render the same balance identically. The others are labelled because a bare
 * `25` in a column headed "Measured" is ambiguous in a way `$2,500.00` is not.
 */
export function formatValue(unit: ThresholdUnit, value: number): string {
  switch (unit) {
    case 'money':
      return formatMoney(value)
    case 'days':
      return `${value} day${value === 1 ? '' : 's'}`
    case 'bps':
      return `${(value / 100).toFixed(1)}%`
    case 'count':
      return String(value)
  }
}
